import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useState } from "react";

import useI18N from "../hooks/useI18N";
import { MUIStyled } from "./MUIStyled";
import SABox from "./SABox";
import LazyImage from "./SAImageLoader";
import SAImagePreviewer from "./SAImagePreviewer";

const SliderContainer = MUIStyled(SABox)({
  position: "relative",
  width: "100%",
  height: 280,
  overflow: "hidden",
  borderRadius: 2,
});

const SAImageBlurBackground = MUIStyled(SABox)(() => ({
  position: "absolute",
  width: "100%",
  height: "100%",
  // backgroundImage: `url(${image})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  filter: "blur(12px)",
  transform: "scale(1.1)",
  zIndex: 1,
}));

const SAImageForegroundWrapper = MUIStyled(SABox)({
  position: "relative",
  zIndex: 2,
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const SAImageClickableBox = MUIStyled(SABox)({
  width: "100%",
  height: "100%",
  borderRadius: 2,
  overflow: "hidden",
  cursor: "pointer",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const SAArrowButtonLeft = MUIStyled(IconButton)({
  position: "absolute",
  top: "50%",
  left: 8,
  transform: "translateY(-50%)",
  zIndex: 3,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  boxShadow: 2,
  "&:hover": { background: "rgba(0, 0, 0, 0.7)" },
});

const SAArrowButtonRight = MUIStyled(IconButton)({
  position: "absolute",
  top: "50%",
  right: 8,
  transform: "translateY(-50%)",
  zIndex: 3,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  boxShadow: 2,
  "&:hover": { background: "rgba(0, 0, 0, 0.7)" },
});

const SADotNavContainer = MUIStyled(SABox)(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  marginTop: 8,
}));

const SADot = MUIStyled(SABox, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.grey[400],
  margin: "0 4px",
  cursor: "pointer",
  transition: "all 0.3s",
}));

const SAImageSlider = ({ images = [], onChange }) => {
  const { t } = useI18N();
  const [currentImg, setCurrentImg] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleNext = () => {
    const next = currentImg < images.length - 1 ? currentImg + 1 : 0;
    setCurrentImg(next);
    onChange?.(next);
  };

  const handlePrev = () => {
    const prevIndex = currentImg > 0 ? currentImg - 1 : images.length - 1;
    setCurrentImg(prevIndex);
    onChange?.(prevIndex);
  };

  const handleDotClick = (index) => {
    setCurrentImg(index);
    onChange?.(index);
  };

  if (!images.length) return <SABox>{t("no-images-available")}</SABox>;

  return (
    <>
      <SliderContainer>
        <SAImageBlurBackground image={images[currentImg]?.url} />
        <SAImageForegroundWrapper>
          <SAImageClickableBox onClick={() => setPreviewOpen(true)}>
            <LazyImage
              src={images[currentImg]?.url}
              alt={`Image ${currentImg + 1}`}
              previewImage={false}
              style={{
                height: "100%",
                width: "auto",
                objectFit: "cover",
                display: "block",
              }}
            />
          </SAImageClickableBox>
        </SAImageForegroundWrapper>

        {images.length > 1 && (
          <>
            <SAArrowButtonLeft onClick={handlePrev}>
              <ChevronLeft fontSize="small" color="white" />
            </SAArrowButtonLeft>

            <SAArrowButtonRight onClick={handleNext}>
              <ChevronRight fontSize="small" color="white" />
            </SAArrowButtonRight>
          </>
        )}

        <SAImagePreviewer
          src={images?.[currentImg]?.url}
          slider={images.length > 1}
          open={previewOpen}
          onNext={handleNext}
          onPrevious={handlePrev}
          onClose={() => setPreviewOpen(false)}
          currentIndex={currentImg}
        />
      </SliderContainer>
      {images.length > 1 && (
        <SADotNavContainer>
          {images.map((_, index) => (
            <SADot
              key={index}
              active={currentImg === index}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </SADotNavContainer>
      )}
    </>
  );
};

export default SAImageSlider;
